import { STANDARD_DATASOURCES, ALL_FRAUD_DATASOURCES } from '../../../common/support/eo_mapping';
import InFilter from '../../../common/support/utils/druid/filters/InFilter';
import {
  LAST_ATTRIBUTED_TOUCH_DATA_TILDE_FEATURE,
  LAST_ATTRIBUTED_TOUCH_TYPE,
} from '../../../../../shared/constants/cenariusDimensions';
import NotFilter from '../../../common/support/utils/druid/filters/NotFilter';

// dropdown values for compare by and filter rows
export const FILTER_TYPES_LIST = [
  'last_attributed_touch_data_tilde_advertising_partner_name',
  'last_attributed_touch_data_dollar_3p',
  'last_attributed_touch_data_tilde_secondary_publisher',
  'last_attributed_touch_data_tilde_agency',
  'last_attributed_touch_data_tilde_agency_id',
  'last_attributed_touch_data_tilde_campaign',
  'last_attributed_touch_data_tilde_customer_campaign',
  'last_attributed_touch_data_tilde_keyword_id',
  'user_data_geo_country_code',
  'user_data_os',
  'user_data_os_version',
  'user_data_geo_dma_code',
  'user_data_brand',
  'user_data_geo_country_en',
  'user_data_geo_region_en',
  'fraud_names',
  'fraud_codes',
  'event_data_custom_param_1',
  'user_data_app_store',
];

// same as FILTER_TYPES_LIST but without fraud_names and fraud_codes so they can be hidden behind a flipper
// will be removed when we roll out to all customers
export const LEGACY_FILTER_TYPES_LIST = [
  'last_attributed_touch_data_tilde_advertising_partner_name',
  'last_attributed_touch_data_dollar_3p',
  'last_attributed_touch_data_tilde_secondary_publisher',
  'user_data_geo_country_code',
  'user_data_os',
  'user_data_geo_dma_code',
  'user_data_brand',
  'user_data_geo_country_en',
  'user_data_geo_region_en',
];

// default selected columns for all blocked event dropdown options
// NOTE: the order of outer columns and inner subcolumns is important
//       see generateColumnHierarchy in common/support/utils/table_helpers.js for more context
export const BLOCKED_EVENTS_SELECTED_NONDIMENSION_COLUMNS = [
  { key: 'eo_click_plus_blocked', type: 'formula' },
  { key: 'eo_click_blocked', type: 'data_sources' },
  { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
  { key: 'eo_install_plus_blocked', type: 'formula' },
  { key: 'eo_install_blocked', type: 'data_sources' },
  { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
  { key: 'eo_open_plus_blocked', type: 'formula' },
  { key: 'eo_open_blocked', type: 'data_sources' },
  { key: 'eo_open_blocked_to_eo_open_total', type: 'formula' },
  { key: 'eo_reinstall_plus_blocked', type: 'formula' },
  { key: 'eo_reinstall_blocked', type: 'data_sources' },
  { key: 'eo_reinstall_blocked_to_eo_reinstall_total', type: 'formula' },
];

const BLOCKED_EVENTS_SELECTED_COLUMNS = [
  { key: 'fraud_names', type: 'dimension' },
  ...BLOCKED_EVENTS_SELECTED_NONDIMENSION_COLUMNS,
];

// names of fraud rules - used to set filters on Blocked Event dropdown selection
export const FRAUD_RULE_NAMES = [
  'conversion_time',
  'click_injection',
  'device_conflict',
  'geo_conflict',
  'ip',
  'once_ever_capped',
  'persona_fraud',
  'suspicious_device',
  'custom',
];

// informational tooltips for fraud name values
export const FRAUD_NAME_TO_INFO = {
  'Geo Conflict': 'The touch and the conversion occur in different countries',
  'Device Conflict': 'The device information on the touch and the conversion are different',
  IP: 'Events coming from known TOR networks',
  'Conversion Time': 'The time between the touch and the conversion is short',
  'Persona Fraud': 'Browsers or devices with suspicious behavior based on Branch’s cross-platform persona graph',
  'Once Ever Capped': 'Events only occurring once per user',
  'Click Injection': 'Events with suspicious touch to Play Store Install Begin times, indicative of Click Injection',
  'Suspicious Device': 'Install from fake device or emulator',
};

// dropdown options for 'Show me blocked events for' dropdown
export const BLOCKED_EVENTS_CONFIG = {
  all_fraud_rules: {
    label: 'All Fraud Rules',
    value: 'all_fraud_rules',
    selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
    dimensions: ['fraud_names'],
    activeHeader: 'fraud_names',
    tooltip: 'See the blocked event count for each enabled fraud rule',
  },
  // COMBINED FRAUD RULES NOT YET SUPPORTED (coming with EG-2399 which is blocked by DATAP-2752)
  // combined_fraud_rules: {
  //   label: 'Combined Fraud Rules',
  //   value: 'combined_fraud_rules',
  //   selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
  //   dimensions: ['fraud_names'],
  //   activeHeader: 'fraud_names',
  //   tooltip: 'Multiple fraud rules can be responsible for a single blocked event. This allows you to see the combination of rules that resulted in a blocked event'
  // },
  conversion_time: {
    label: 'Conversion Time',
    value: 'conversion_time',
    selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
    dimensions: ['fraud_names'],
    activeHeader: 'eo_install_blocked',
    tooltip: FRAUD_NAME_TO_INFO['Conversion Time'],
  },
  click_injection: {
    label: 'Click Injection',
    value: 'click_injection',
    selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
    dimensions: ['fraud_names'],
    activeHeader: 'eo_install_blocked',
    tooltip: FRAUD_NAME_TO_INFO['Click Injection'],
  },
  device_conflict: {
    label: 'Device Conflict',
    value: 'device_conflict',
    selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
    dimensions: ['fraud_names'],
    activeHeader: 'eo_install_blocked',
    tooltip: FRAUD_NAME_TO_INFO['Device Conflict'],
  },
  geo_conflict: {
    label: 'Geo Conflict',
    value: 'geo_conflict',
    selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
    dimensions: ['fraud_names'],
    activeHeader: 'eo_install_blocked',
    tooltip: FRAUD_NAME_TO_INFO['Geo Conflict'],
  },
  ip: {
    label: 'IP',
    value: 'ip',
    selectedColumns: [
      { key: 'fraud_names', type: 'dimension' },
      { key: 'attributed', type: 'dimension' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_open_blocked', type: 'data_sources' },
      { key: 'eo_open_blocked_to_eo_open_total', type: 'formula' },
      { key: 'eo_reinstall_blocked', type: 'data_sources' },
      { key: 'eo_reinstall_blocked_to_eo_reinstall_total', type: 'formula' },
    ],
    dimensions: ['fraud_names', 'attributed'],
    activeHeader: 'eo_install_blocked',
    organic: true,
    tooltip: FRAUD_NAME_TO_INFO.IP,
  },
  once_ever_capped: {
    label: 'Once Ever Capped',
    value: 'once_ever_capped',
    selectedColumns: [
      { key: 'fraud_names', type: 'dimension' },
      { key: 'attributed', type: 'dimension' },
      { key: 'COMPLETE_REGISTRATION_BLOCKED', type: 'standard_event' },
      { key: 'COMPLETE_TUTORIAL_BLOCKED', type: 'standard_event' },
    ],
    dimensions: ['fraud_names', 'attributed'],
    activeHeader: 'COMPLETE_REGISTRATION_BLOCKED',
    organic: true,
    tooltip: FRAUD_NAME_TO_INFO['Once Ever Capped'],
  },
  persona_fraud: {
    label: 'Persona Fraud',
    value: 'persona_fraud',
    selectedColumns: [
      { key: 'fraud_names', type: 'dimension' },
      { key: 'attributed', type: 'dimension' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_open_blocked', type: 'data_sources' },
      { key: 'eo_open_blocked_to_eo_open_total', type: 'formula' },
      { key: 'eo_reinstall_blocked', type: 'data_sources' },
      { key: 'eo_reinstall_blocked_to_eo_reinstall_total', type: 'formula' },
    ],
    dimensions: ['fraud_names', 'attributed'],
    activeHeader: 'eo_click_blocked',
    organic: true,
    tooltip: FRAUD_NAME_TO_INFO['Persona Fraud'],
  },
  suspicious_device: {
    label: 'Suspicious Device',
    value: 'suspicious_device',
    selectedColumns: [
      { key: 'fraud_names', type: 'dimension' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
    ],
    dimensions: ['fraud_names'],
    activeHeader: 'eo_install_blocked',
    organic: false,
    tooltip: FRAUD_NAME_TO_INFO['Suspicious Device'],
  },
  custom: {
    label: 'Custom',
    value: 'custom',
    selectedColumns: BLOCKED_EVENTS_SELECTED_COLUMNS,
    dimensions: ['fraud_names'],
    activeHeader: 'eo_install_blocked',
    tooltip: 'Block on any attribute(s) stored at the event level',
  },
  none: {
    label: 'None',
    value: 'none',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_open_plus_blocked', type: 'formula' },
      { key: 'eo_open_blocked', type: 'data_sources' },
      { key: 'eo_open_blocked_to_eo_open_total', type: 'formula' },
      { key: 'eo_reinstall_plus_blocked', type: 'formula' },
      { key: 'eo_reinstall_blocked', type: 'data_sources' },
      { key: 'eo_reinstall_blocked_to_eo_reinstall_total', type: 'formula' },
    ],
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
    activeHeader: 'eo_install_blocked',
  },
};

// dropdown options for 'Show me key fraud indicators for' dropdown
export const FRAUD_INDICATORS_CONFIG = {
  ad_stacking: {
    label: 'Ad Stacking',
    value: 'ad_stacking',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_click_to_eo_install', type: 'formula' },
    ],
    activeHeader: 'eo_click_to_eo_install',
    tooltip:
      'Publishers will sometimes put dozens of ads in one ad placement, causing high numbers of impressions and clicks, with low install rates.',
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
  },
  click_flooding: {
    label: 'Click Flooding',
    value: 'click_flooding',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_click_to_eo_install', type: 'formula' },
      {
        key: 'eo_install_days_from_last_attributed_touch_to_event_under_1',
        type: 'formula',
      },
      {
        key: 'eo_install_days_from_last_attributed_touch_to_event_over_1',
        type: 'formula',
      },
    ],
    activeHeader: 'eo_install_days_from_last_attributed_touch_to_event_over_1',
    tooltip:
      'Publishers can generate millions of clicks with different device IDs in the hope that one of those users will install later. Generally the click and install actions are disconnected, so you can expect to see long click to install times.',
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
  },
  device_reset: {
    label: 'Device Reset',
    value: 'device_reset',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_click_to_eo_install', type: 'formula' },
    ],
    activeHeader: 'eo_click_plus_blocked',
    tooltip:
      'Device Reset Fraud is characterized by new devices from suspicious IPs. Keep an eye on your user value metrics and suspiciously high click to install rates.',
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
  },
  fake_devices: {
    label: 'Fake Devices / Click Farms',
    value: 'fake_devices',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
    ],
    activeHeader: 'eo_install_blocked',
    tooltip:
      'Emulators and clicks farms generate clicks from suspicious IPs, as well as show abnormal behavior after installing. Check blocked clicks and your user value metrics in Ads Analytics to identify this fraud.',
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
  },
  install_hijacking: {
    label: 'Install Hijacking / Click Injection',
    value: 'install_hijacking',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
      { key: 'eo_click_to_eo_install', type: 'formula' },
      {
        key: 'eo_install_seconds_from_last_attributed_touch_to_event_under_10',
        type: 'formula',
      },
      {
        key: 'eo_install_seconds_from_last_attributed_touch_to_event_between_10_and_30',
        type: 'formula',
      },
      {
        key: 'eo_install_seconds_from_last_attributed_touch_to_event_over_30',
        type: 'formula',
      },
    ],
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
    activeHeader: 'eo_install_seconds_from_last_attributed_touch_to_event_under_10',
    tooltip:
      'When a new app is installed, other apps on the phone can sometimes detect the install, even before the first open. Fraudulent publishers may fire off a click when they detect an organic install, just before the app is opened. This can be identified by suspiciously low click to install times',
  },
  custom: {
    label: 'Custom',
    value: 'custom',
    selectedColumns: [
      {
        key: 'last_attributed_touch_data_tilde_advertising_partner_name',
        type: 'dimension',
      },
      // { key: 'fraud_names', type: 'dimension' },
      { key: 'eo_click_plus_blocked', type: 'formula' },
      { key: 'eo_click_blocked', type: 'data_sources' },
      { key: 'eo_click_blocked_to_eo_click_total', type: 'formula' },
      { key: 'eo_install_plus_blocked', type: 'formula' },
      { key: 'eo_install_blocked', type: 'data_sources' },
      { key: 'eo_install_blocked_to_eo_install_total', type: 'formula' },
    ],
    dimensions: ['last_attributed_touch_data_tilde_advertising_partner_name'],
    activeHeader: 'eo_install_seconds_from_last_attributed_touch_to_event_under_10',
  },
};

export const DEFAULT_DIMENSIONS = ['fraud_names'];

// will be removed when we roll out to all customers
export const LEGACY_DEFAULT_DIMENSIONS = ['last_attributed_touch_data_tilde_advertising_partner_name'];

export const DEFAULT_BLOCKED_EVENT_INDICATOR = 'all_fraud_rules';

export const DEFAULT_FRAUD_INDICATOR = 'custom';

export const DEFAULT_ACTIVE_HEADER = 'fraud_names';

// will be removed when we roll out to all customers
export const LEGACY_DEFAULT_ACTIVE_HEADER = 'eo_install_seconds_from_last_attributed_touch_to_event_under_10';

export const FORMULAS_LIST = [...ALL_FRAUD_DATASOURCES, ...Object.keys(STANDARD_DATASOURCES)];

export const FRAUD_SOURCE_QUERIES = {
  eo_install: {
    dimensions: ['seconds_from_last_attributed_touch_to_event', 'days_from_last_attributed_touch_to_event'],
    filters: {
      '!last_attributed_touch_data_tilde_advertising_partner_name': [null],
    },
  },
  eo_click: {
    filters: {
      '!last_attributed_touch_data_tilde_advertising_partner_name': [null],
    },
  },
  eo_click_blocked: {
    filters: {
      '!last_attributed_touch_data_tilde_advertising_partner_name': [null],
    },
  },
  eo_install_blocked: {
    filters: {
      '!last_attributed_touch_data_tilde_advertising_partner_name': [null],
    },
  },
};

export const BASELINE_QUERIES = {
  'all attributed installs': {
    filter: new InFilter(LAST_ATTRIBUTED_TOUCH_TYPE, ['CLICK']),
  },
  'all paid advertising': {
    filter: new InFilter(LAST_ATTRIBUTED_TOUCH_DATA_TILDE_FEATURE, ['paid advertising']),
  },
  'all non paid advertising': {
    filter: new NotFilter(new InFilter(LAST_ATTRIBUTED_TOUCH_DATA_TILDE_FEATURE, ['paid advertising', null])),
  },
};

export const DOMAIN_MAP = {
  seconds_from_last_attributed_touch_to_event: {
    span: [0, 60],
    interval: 5,
  },
  minutes_from_last_attributed_touch_to_event: {
    span: [0, 60],
    interval: 5,
  },
  hours_from_last_attributed_touch_to_event: {
    span: [0, 24],
    interval: 1,
  },
  days_from_last_attributed_touch_to_event: {
    span: [0, 30],
    interval: 1,
  },
};

export const DIMENSIONS_REQUIRED_FOR_COLUMNS = {
  long_cti_times: {
    source: 'eo_install',
    dimension: 'days_from_last_attributed_touch_to_event',
  },
  short_cti_times: {
    source: 'eo_install',
    dimension: 'seconds_from_last_attributed_touch_to_event',
  },
};

export const FRAUD_VALUES = ['fraud_names', 'fraud_codes'];
